import { AFDataObject, AFMessageType } from '.';

export interface StatisticsRawData {
  firstFlowTimestamp: Date;
  athTokenAmountInDecimals: number;
  currentTokenAmountInDecimals: number;
  athAfCnt: number;
  currentAfCnt: number;

  athLafTokenAmountInDecimals: number;
  currentLafTokenAmountInDecimals: number;
  athLafCnt: number;
  currentLafCnt: number;

  atlMc: number;
  athMc: number;
  totalUsedUsd: number;
  lastFlow: {
    priceInUsd: number;
    mc: number;
    tokenAmountInDecimals: number;
    flowActionType: string;
    flowType: string;
    afAddress: string;
  };
  recentFlows: RecentFlow[];
}

export interface RecentFlow {
  timestamp: Date;
  totalTokenRawAmount: number; // 데시멜이 적용된 값
  afAddressCnt: number;
  cummulatedUsed: number;
  hasOrderIssue: boolean;
  lafTotalTokenRawAmount: number;
  lafCummulatedUsed: number;
  lafAddressCnt: number;
}

export interface AddressTokenAmount {
  address: string;
  tokenAmount: number;
}

export interface StatisticsStateData extends StatisticsRawData {
  lastUpdated: Date;
}

export class Statistics implements AFDataObject<StatisticsRawData, StatisticsStateData> {
  public firstFlowTimestamp: Date;
  public athTokenAmountInDecimals: number;
  public currentTokenAmountInDecimals: number;
  public athAfCnt: number;
  public currentAfCnt: number;
  public athLafTokenAmountInDecimals: number;
  public currentLafTokenAmountInDecimals: number;
  public athLafCnt: number;
  public currentLafCnt: number;
  public atlMc: number;
  public athMc: number;
  public totalUsedUsd: number;
  public lastFlow: {
    priceInUsd: number;
    mc: number;
    tokenAmountInDecimals: number;
    flowActionType: string;
    flowType: string;
    afAddress: string;
  };
  public lastUpdated: Date;
  // 새로운 속성들 추가
  public recentFlows: RecentFlow[];

  constructor(data?: StatisticsRawData | StatisticsStateData | null, lastUpdated?: Date,) {
    // 기본값 설정
    this.firstFlowTimestamp = data?.firstFlowTimestamp ? new Date(data.firstFlowTimestamp) : new Date(0);
    this.athTokenAmountInDecimals = data?.athTokenAmountInDecimals || 0;
    this.currentTokenAmountInDecimals = data?.currentTokenAmountInDecimals || 0;
    this.athAfCnt = data?.athAfCnt || 0;
    this.currentAfCnt = data?.currentAfCnt || 0;
    this.athLafTokenAmountInDecimals = data?.athLafTokenAmountInDecimals || 0;
    this.currentLafTokenAmountInDecimals = data?.currentLafTokenAmountInDecimals || 0;
    this.athLafCnt = data?.athLafCnt || 0;
    this.currentLafCnt = data?.currentLafCnt || 0;
    this.atlMc = data?.atlMc || 0;
    this.athMc = data?.athMc || 0;
    this.totalUsedUsd = data?.totalUsedUsd || 0;
    this.lastFlow = {
      priceInUsd: data?.lastFlow?.priceInUsd || 0,
      mc: data?.lastFlow?.mc || 0,
      tokenAmountInDecimals: data?.lastFlow?.tokenAmountInDecimals || 0,
      flowActionType: data?.lastFlow?.flowActionType || 'SWAP',
      flowType: data?.lastFlow?.flowType || 'INFLOW',
      afAddress: data?.lastFlow?.afAddress || ''
    };
    this.lastUpdated = new Date(lastUpdated || 0);
    this.recentFlows = this.calculateRecentFlows(data?.recentFlows || []);
  }

  static create(data?: StatisticsRawData | StatisticsStateData | null, lastUpdated?: Date): Statistics {
    return new Statistics(data, lastUpdated);
  }

  getMessageType(): AFMessageType {
    return AFMessageType.TOKEN_STATISTICS;
  }

  // private calculateLegendaryAfCnt(): number {
  //   const { isLegendaryAddress } = useAFStore.getState();
  //   return this.currentHoldingAddresses.filter(address => 
  //     isLegendaryAddress(address)
  //   ).length;
  // }

  private calculateRecentFlows(recentFlows: RecentFlow[]): RecentFlow[] {
    // hasOrderIssue 기본값 처리
    
    const flowsWithDefaults = recentFlows.map(flow => {
      return {
        ...flow,
        hasOrderIssue: flow.hasOrderIssue ?? false
      }
    });
    // 시간순으로 정렬 (오래된 순서대로)
    return flowsWithDefaults.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  toRawData(): StatisticsRawData {
    return {
      firstFlowTimestamp: new Date(this.firstFlowTimestamp),
      athTokenAmountInDecimals: this.athTokenAmountInDecimals,
      currentTokenAmountInDecimals: this.currentTokenAmountInDecimals,
      athAfCnt: this.athAfCnt,
      currentAfCnt: this.currentAfCnt,
      athLafTokenAmountInDecimals: this.athLafTokenAmountInDecimals,
      currentLafTokenAmountInDecimals: this.currentLafTokenAmountInDecimals,
      athLafCnt: this.athLafCnt,
      currentLafCnt: this.currentLafCnt,
      atlMc: this.atlMc,
      athMc: this.athMc,
      totalUsedUsd: this.totalUsedUsd,
      lastFlow: {
        priceInUsd: this.lastFlow.priceInUsd,
        mc: this.lastFlow.mc,
        tokenAmountInDecimals: this.lastFlow.tokenAmountInDecimals,
        flowActionType: this.lastFlow.flowActionType,
        flowType: this.lastFlow.flowType,
        afAddress: this.lastFlow.afAddress
      },
      recentFlows: this.recentFlows
    };
  }

  toStateData(): StatisticsStateData {
    return {
      ...this.toRawData(),
      lastUpdated: this.lastUpdated,
    };
  }

  update(data: StatisticsRawData, lastUpdated?: Date): void {
    this.firstFlowTimestamp = new Date(data.firstFlowTimestamp);
    this.athTokenAmountInDecimals = data.athTokenAmountInDecimals;
    this.currentTokenAmountInDecimals = data.currentTokenAmountInDecimals;
    this.athAfCnt = data.athAfCnt;
    this.currentAfCnt = data.currentAfCnt;
    this.athLafTokenAmountInDecimals = data.athLafTokenAmountInDecimals;
    this.currentLafTokenAmountInDecimals = data.currentLafTokenAmountInDecimals;
    this.athLafCnt = data.athLafCnt;
    this.currentLafCnt = data.currentLafCnt;
    this.atlMc = data.atlMc;
    this.athMc = data.athMc;
    this.totalUsedUsd = data.totalUsedUsd;
    this.lastFlow = {
      priceInUsd: data.lastFlow.priceInUsd,
      mc: data.lastFlow.mc,
      tokenAmountInDecimals: data.lastFlow.tokenAmountInDecimals,
      flowActionType: data.lastFlow.flowActionType,
      flowType: data.lastFlow.flowType,
      afAddress: data.lastFlow.afAddress
    };
    this.lastUpdated = new Date(lastUpdated || Date.now());
    this.recentFlows = this.calculateRecentFlows(data.recentFlows || []);
  }

  /**
   * recentFlows 배열에서 hasOrderIssue가 true인 항목이 하나라도 있는지 확인
   */
  hasOrderIssue(): boolean {
    return this.recentFlows.some(flow => flow.hasOrderIssue === true);
  }
  filter(existingData: StatisticsStateData): boolean {
    return Number(this.lastUpdated) > Number(existingData.lastUpdated);
  }
}
